/**
 * ExtendedForecastPanel · v2.7.34 (2026-02-21)
 * ═════════════════════════════════════════════════════════════════════
 * 8-day + 12-day extended forecast tab inside Weather Commander.
 * Mirrors the same data shown on the public /local-weather page.
 *
 * The backend cron refreshes Open-Meteo once per 24 h; this component
 * just reads the snapshot. A manual Refresh button forces an upstream
 * pull if the operator wants the latest numbers between syncs.
 */
import { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RefreshCw, CalendarDays, Sun, CloudRain, Snowflake, Cloud, CloudLightning, CloudFog, CloudSun, CloudDrizzle, CloudSnow } from "lucide-react";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";

const ICONS = {
  "sun": Sun,
  "cloud-sun": CloudSun,
  "cloud": Cloud,
  "cloud-fog": CloudFog,
  "cloud-drizzle": CloudDrizzle,
  "cloud-rain": CloudRain,
  "cloud-snow": CloudSnow,
  "snowflake": Snowflake,
  "cloud-lightning": CloudLightning,
};

function dayLabel(iso, idx) {
  if (!iso) return "";
  const d = new Date(iso + "T12:00:00");
  if (idx === 0) return "Today";
  if (idx === 1) return "Tomorrow";
  return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
}

export default function ExtendedForecastPanel({ embedded = false }) {
  const [rows8, setRows8] = useState([]);
  const [rows12, setRows12] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [busy, setBusy] = useState(false);
  // v2.7.35 · CMS-configured background slots
  const [bg8, setBg8] = useState(null);
  const [bg12, setBg12] = useState(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  const reload = useCallback(async () => {
    setBusy(true);
    try {
      const [a, b, c] = await Promise.all([
        adminApi.get("/admin/weather/forecast/extended?days=8"),
        adminApi.get("/admin/weather/forecast/extended?days=12"),
        adminApi.get("/admin/weather/cms/config"),
      ]);
      setRows8(a.data?.rows || []);
      setRows12(b.data?.rows || []);
      setFetchedAt(a.data?.fetched_at || null);
      const au = c.data?.asset_urls || {};
      setBg8(au.forecast_8day_bg ? `${API}${au.forecast_8day_bg}` : null);
      setBg12(au.forecast_12day_bg ? `${API}${au.forecast_12day_bg}` : null);
    } catch (e) {
      toast.error(`Forecast load failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  }, [API]);
  useEffect(() => { reload(); const id = setInterval(reload, 24 * 60 * 60 * 1000); return () => clearInterval(id); }, [reload]);

  const force = async () => {
    setBusy(true);
    try {
      await adminApi.post("/admin/weather/forecast/refresh");
      toast.success("Extended forecast refreshed from Open-Meteo");
      reload();
    } catch (e) {
      toast.error(`Refresh failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="extended-forecast-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-2">
          <CalendarDays className="h-3.5 w-3.5 text-sky-300"/> Extended forecast · Open-Meteo (ECMWF + GFS blend) · OUN forecast area
        </div>
        <div className="flex items-center gap-3 text-[10px] text-zinc-500">
          <span>Last sync: {fetchedAt ? new Date(fetchedAt).toLocaleString() : "—"}</span>
          <Button size="sm" onClick={force} disabled={busy} className="bg-amber-400 text-zinc-900 hover:bg-amber-300 font-bold h-7 text-xs" data-testid="forecast-refresh-btn">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5"/>Force sync
          </Button>
        </div>
      </div>

      <ForecastGrid title="8-Day Forecast" rows={rows8} testId="forecast-8day" bgUrl={bg8}/>
      <ForecastGrid title="12-Day Forecast" rows={rows12} testId="forecast-12day" bgUrl={bg12}/>
    </div>
  );
}

function ForecastGrid({ title, rows, testId, bgUrl }) {
  // v2.7.35 · Optional CMS background image renders behind the entire
  // bounding box (not the individual day cells) per spec.
  const cardStyle = bgUrl ? {
    backgroundImage: `linear-gradient(rgba(9,9,11,0.78), rgba(9,9,11,0.78)), url("${bgUrl}")`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  } : undefined;
  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid={testId} style={cardStyle}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 mb-3 border-b border-amber-400/40 flex items-center justify-between">
        <span>{title}</span>
        <Badge className="bg-zinc-950 border-zinc-700 text-zinc-300">{rows.length} days</Badge>
      </div>
      {rows.length === 0 && <div className="text-sm text-zinc-500 py-4 text-center">Loading forecast…</div>}
      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-12 gap-2">
        {rows.map((r, idx) => {
          const Icon = ICONS[r.icon] || Cloud;
          const lightning = r.icon === "cloud-lightning";
          return (
            <div
              key={r.date}
              className={`rounded-lg p-2.5 text-center border ${lightning ? "border-rose-400/40 bg-rose-950/30" : "border-zinc-800 bg-zinc-950/60"}`}
              data-testid={`${testId}-day-${idx}`}
            >
              <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold truncate" title={r.date}>
                {dayLabel(r.date, idx)}
              </div>
              <Icon className={`h-7 w-7 mx-auto my-1.5 ${lightning ? "text-rose-300" : "text-sky-300"}`}/>
              <div className="text-[10px] text-zinc-500 truncate" title={r.label}>{r.label}</div>
              <div className="mt-1.5 flex items-baseline justify-center gap-1 tabular-nums">
                <span className="text-base font-extrabold text-zinc-100">{Math.round(r.temp_max_f)}°</span>
                <span className="text-[10px] text-zinc-500">{Math.round(r.temp_min_f)}°</span>
              </div>
              {r.precip_prob_max > 0 && (
                <div className="text-[10px] text-sky-300 mt-1 flex items-center justify-center gap-0.5">
                  <CloudRain className="h-3 w-3"/> {r.precip_prob_max}%
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
